/**
 * Reports period keys and helpers.
 *
 * Live report pages send either a predefined `period` key OR custom `from`/`to`
 * (never both). Bounds for predefined keys are resolved on the backend.
 *
 * Custom UI dates are inclusive; API `to` is exclusive half-open (+1 day).
 */

export const PERIOD_CURRENT_MONTH = 'current-month'
export const PERIOD_CURRENT_TERM = 'current-term'
export const PERIOD_ACADEMIC_YEAR = 'academic-year'
export const PERIOD_ALL_TIME = 'all-time'
/** UI-only selection mode; never sent as API `period`. */
export const PERIOD_CUSTOM = 'custom'

const pad = n => String(n).padStart(2, '0')
const toIsoDate = d => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`

const utcDate = (year, monthIndex, day = 1) => new Date(Date.UTC(year, monthIndex, day))

const parseIsoDateParts = iso => {
	if (!iso || typeof iso !== 'string') return null
	const parts = iso.split('-')
	if (parts.length !== 3) return null
	const year = Number(parts[0])
	const month = Number(parts[1])
	const day = Number(parts[2])
	if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
		return null
	}
	const d = new Date(Date.UTC(year, month - 1, day))
	if (
		d.getUTCFullYear() !== year ||
		d.getUTCMonth() !== month - 1 ||
		d.getUTCDate() !== day
	) {
		return null
	}
	return { year, month, day, date: d }
}

/** Inclusive UI end date → exclusive API `to` (half-open). */
export const inclusiveEndToExclusiveApiTo = inclusiveToIso => {
	const parts = parseIsoDateParts(inclusiveToIso)
	if (!parts) return null
	const next = new Date(parts.date)
	next.setUTCDate(next.getUTCDate() + 1)
	return toIsoDate(next)
}

export const isValidInclusiveCustomRange = (fromIso, toIso) => {
	const from = parseIsoDateParts(fromIso)
	const to = parseIsoDateParts(toIso)
	if (!from || !to) return false
	return from.date.getTime() <= to.date.getTime()
}

export const isCustomPeriod = key => String(key || '').trim() === PERIOD_CUSTOM

/**
 * Build insights query params: predefined `{ period }` XOR custom `{ from, to }`.
 * Returns null when custom is selected but the inclusive range is invalid.
 */
export const buildInsightsDateParams = ({ period, customFrom, customTo } = {}) => {
	if (isCustomPeriod(period)) {
		if (!isValidInclusiveCustomRange(customFrom, customTo)) return null
		const exclusiveTo = inclusiveEndToExclusiveApiTo(customTo)
		if (!exclusiveTo) return null
		return { from: customFrom, to: exclusiveTo }
	}
	const key = String(period || '').trim()
	if (!isValidReportPeriod(key) || isCustomPeriod(key)) {
		return { period: PERIOD_ACADEMIC_YEAR }
	}
	return { period: key }
}

export const academicYearBounds = (today = new Date()) => {
	const year = today.getUTCFullYear()
	const month = today.getUTCMonth() + 1
	const startYear = month >= 8 ? year : year - 1
	return {
		from: utcDate(startYear, 7, 1),
		to: utcDate(startYear + 1, 7, 1),
	}
}

/** Short academic-year span for UI labels, e.g. "2026-27". */
export const formatAcademicYearShort = (today = new Date()) => {
	const startYear = academicYearBounds(today).from.getUTCFullYear()
	const endShort = String((startYear + 1) % 100).padStart(2, '0')
	return `${startYear}-${endShort}`
}

/** Toolbar / dropdown label, e.g. "Academic year 2026-27". */
export const getAcademicYearPeriodLabel = (today = new Date()) =>
	`Academic year ${formatAcademicYearShort(today)}`

export const REPORT_PERIODS = [
	{ value: PERIOD_CURRENT_MONTH, label: 'This month' },
	// Current term: temporarily hidden from Reports UI (backend support retained).
	// { value: PERIOD_CURRENT_TERM, label: 'Current term' },
	{ value: PERIOD_ACADEMIC_YEAR, label: getAcademicYearPeriodLabel() },
	{ value: PERIOD_ALL_TIME, label: 'All time' },
	{ value: PERIOD_CUSTOM, label: 'Custom period' },
]

export const currentMonthBounds = (today = new Date()) => {
	const year = today.getUTCFullYear()
	const monthIndex = today.getUTCMonth()
	return {
		from: utcDate(year, monthIndex, 1),
		to: utcDate(year, monthIndex + 1, 1),
	}
}

export const currentTermBounds = (today = new Date()) => {
	const { from: ayStart, to: ayEnd } = academicYearBounds(today)
	const term2 = utcDate(ayStart.getUTCFullYear(), 11, 1)
	const term3 = utcDate(ayStart.getUTCFullYear() + 1, 3, 1)
	if (today < term2) return { from: ayStart, to: term2 }
	if (today < term3) return { from: term2, to: term3 }
	return { from: term3, to: ayEnd }
}

export const resolveReportPeriod = (periodKey = PERIOD_ACADEMIC_YEAR, today = new Date()) => {
	const key = String(periodKey || PERIOD_ACADEMIC_YEAR).trim()
	if (key === PERIOD_ALL_TIME) {
		return {
			key,
			from: null,
			to: null,
			granularity: null,
			timezone: 'UTC',
			unbounded: true,
		}
	}
	let bounds
	if (key === PERIOD_CURRENT_MONTH) bounds = currentMonthBounds(today)
	else if (key === PERIOD_CURRENT_TERM) bounds = currentTermBounds(today)
	else if (key === PERIOD_ACADEMIC_YEAR) bounds = academicYearBounds(today)
	else throw new Error(`Invalid period '${key}'`)

	return {
		key,
		from: toIsoDate(bounds.from),
		to: toIsoDate(bounds.to),
		granularity: 'month',
		timezone: 'UTC',
		unbounded: false,
	}
}

/** True for predefined API period keys (not UI-only custom). */
export const isValidReportPeriod = key =>
	[
		PERIOD_CURRENT_MONTH,
		PERIOD_CURRENT_TERM,
		PERIOD_ACADEMIC_YEAR,
		PERIOD_ALL_TIME,
	].includes(String(key || '').trim())

/** True for toolbar selection including custom. */
export const isSelectableReportPeriod = key =>
	isValidReportPeriod(key) || isCustomPeriod(key)

const MONTH_SHORT = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
]

/** Format ISO YYYY-MM-DD for report UI: "1 Sep 2026" (no hyphens). */
export const formatReportDisplayDate = iso => {
	const parts = parseIsoDateParts(iso)
	if (!parts) return ''
	return `${parts.day} ${MONTH_SHORT[parts.month - 1]} ${parts.year}`
}

/** Inclusive custom range for report UI: "1 Sep 2026 – 30 Sep 2026". */
export const formatReportDisplayDateRange = (fromIso, toIso) => {
	const fromLabel = formatReportDisplayDate(fromIso)
	const toLabel = formatReportDisplayDate(toIso)
	if (!fromLabel || !toLabel) return ''
	return `${fromLabel} – ${toLabel}`
}

/** Human label for Hero / copy that mirrors the toolbar period control. */
export const getReportPeriodLabel = (periodKey, { customFrom, customTo, today } = {}) => {
	if (periodKey === PERIOD_CURRENT_MONTH) return 'This month'
	if (periodKey === PERIOD_CURRENT_TERM) return 'Current term'
	if (periodKey === PERIOD_ACADEMIC_YEAR) return getAcademicYearPeriodLabel(today)
	if (periodKey === PERIOD_ALL_TIME) return 'All time'
	if (isCustomPeriod(periodKey)) {
		if (isValidInclusiveCustomRange(customFrom, customTo)) {
			return formatReportDisplayDateRange(customFrom, customTo)
		}
		return 'Custom period'
	}
	const match = REPORT_PERIODS.find(p => p.value === periodKey)
	return match?.label || 'selected period'
}

/**
 * Period phrase for sentence-style titles.
 * Named periods are lowercased; custom date ranges keep title-case months.
 */
export const getReportPeriodTitlePhrase = (periodKey, range = {}) => {
	const label = getReportPeriodLabel(periodKey, range)
	if (isCustomPeriod(periodKey) && isValidInclusiveCustomRange(range.customFrom, range.customTo)) {
		return label
	}
	return label.toLowerCase()
}
